import { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { TextField, TableRow, Autocomplete, Tooltip, TableCell, Typography } from "@mui/material";
import { UXDataTableWithoutBody, StyledTableCell, StyledTableBody } from "../UXDataTable";
import ContainerType from "../../api/LIMS/ContainerType";
import ContainerStatus from "../../api/LIMS/ContainerStatus";
import UnitOfMeasure from "../../api/Admin/UnitOfMeasure";
import Location from "../../api/Admin/Location";
import {
  RoundNumber,
  RecalculateCurrentAmountOnUoMChange,
  RecalculateInitialSizeOnUoMChange,
  CheckContainerCurrentAmount,
  CheckContainerSize,
  isNumber,
  formatMidasNumber,
} from "../../global";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TrashIcon from "@mui/icons-material/Delete";
import EmailAddressTextField from "../EmailAddressField";
import { useUser } from "../../context/UserContext";
import IntegrationCommentsIcon from "@mui/icons-material/IntegrationInstructions";
import ModalTwoButtons from "../../components/Modal/ModalTwoButtons";
import AuthGate from "../../routing/components/AuthGate";
import { usePreferences } from "../../context/PreferencesContext";
import { getGuestContainerRestrictionStatus } from "./guestContainerRestrictions";

const containerGridFontSize = 12;

const StyledAutocomplete = styled(Autocomplete)({
  "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
    fontSize: containerGridFontSize,
  },
});

const Option = styled("li")({
  fontSize: containerGridFontSize,
});

const determineNewContainerOwnerEmail = (
  isGuestUser,
  sampleObject,
  newContainerDefaultOwner,
  userEmail,
) => {
  const isSampleContainer = sampleObject !== null && sampleObject !== undefined;

  if (isGuestUser) {
    return isSampleContainer
      ? sampleObject?.sampleOwnerEmail ?? null
      : newContainerDefaultOwner ?? null;
  }

  return isSampleContainer
    ? sampleObject?.sampleOwnerEmail || newContainerDefaultOwner || userEmail
    : newContainerDefaultOwner || userEmail;
};

const ContainerTable = ({
  width,
  showParentContainer,
  showTests,
  containers,
  setContainers,
  containerErrorChecks,
  setContainerErrorChecks,
  enableAdditions,
  enableDeletions,
  enableCopy,
  availableParentContainers,
  sampleObject,
  substanceObject,
  lockContainerStatus,
  newContainerDefaultOwner,
  readOnly = false,
  showParentMidas = false,
  showWorkRequestTests = false,
  useDefaultLocation = false,
}) => {
  const [containerTypes, setContainerTypes] = useState([]);
  const [containerStatuses, setContainerStatuses] = useState([]);
  const [availableUOMs, setAvailableUOMs] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);

  const [modalCommentsOpen, setModalCommentsOpen] = useState(false);
  const [modalCommentsButton1Text, setModalCommentsButton1Text] = useState(null);
  const [modalCommentsButton2Text, setModalCommentsButton2Text] = useState(null);
  const [modalCommentsTitle, setModalCommentsTitle] = useState(null);
  const [modalCommentsText, setModalCommentsText] = useState(null);
  const [tempContainerInstruction, setTempContainerInstruction] = useState(null);
  const [containerWorkingIndex, setContainerWorkingIndex] = useState(null);

  const { username: userEmail, isGuestUser, isInternalUser } = useUser();
  const { userPreferences: { homeSiteName: userHomeSite } = {} } = usePreferences();

  const { isRestricted, message } = useMemo(
    () => getGuestContainerRestrictionStatus(isGuestUser, userHomeSite, availableLocations),
    [isGuestUser, userHomeSite, availableLocations],
  );

  const defaultLocation =
    isGuestUser && availableLocations?.length > 0 ? availableLocations[0] : null;

  const sortedContainers = useMemo(
    () => [...(containers ?? [])].sort((a, b) => a.containerNumber - b.containerNumber),
    [containers],
  );

  const sortedParentContainers = useMemo(
    () =>
      availableParentContainers
        ? [...availableParentContainers]
          .sort((a, b) => a.containerNumber - b.containerNumber)
          .filter((c) => c.containerStatusName !== "Discarded")
        : [],
    [availableParentContainers],
  );

  const methodCols = [
    "#",
    showParentContainer && "Parent Container",
    showParentMidas && "Parent MIDAS",
    showWorkRequestTests && "Tests",
    "Type",
    "Status",
    "Container Size",
    "Current Amt",
    "UoM",
    "% Full",
    "Location",
    "Sublocation",
    "Owner",
    "Comment",
    showTests && "Tests",
  ].filter(Boolean);

  function closeModalComments() {
    setModalCommentsOpen(false);
    setTempContainerInstruction("");
    setContainerWorkingIndex(null);
  }

  function openModalComments(title, text, buttonText, button2Text, containerIndex) {
    setModalCommentsButton1Text(buttonText);
    setModalCommentsButton2Text(button2Text);
    setModalCommentsOpen(true);
    setModalCommentsTitle(title);
    setModalCommentsText(text);

    setContainerWorkingIndex(containerIndex);
    setTempContainerInstruction(containers[containerIndex].comments);
  }

  useEffect(() => {
    let cancelPromise = false;

    if (isInternalUser) {
      Location.getAll().then((res) => {
        if (cancelPromise) return;
        setAvailableLocations(
          res
            .filter((result) => result.isActive === true)
            .sort((a, b) => a.locationName.localeCompare(b.locationName)),
        );
      });
    } else {
      Location.getBySite(userHomeSite).then((res) => {
        if (cancelPromise) return;
        setAvailableLocations(
          res
            .filter((result) => result.isActive === true)
            .sort((a, b) => a.locationName.localeCompare(b.locationName)),
        );
      });
    }

    UnitOfMeasure.getAll().then((res) => {
      if (cancelPromise) return;
      setAvailableUOMs(
        res
          .filter(
            (result) =>
              (result.metricStandardConversion !== null || result.type === "concentration") &&
              result.isActive === true,
          )
          .sort((a, b) => a.uoMName.localeCompare(b.uoMName)),
      );
    });

    ContainerType.getAll().then((res) => {
      if (cancelPromise) return;
      setContainerTypes(
        res
          .filter((result) => result.isActive === true)
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    });

    ContainerStatus.getAll().then((res) => {
      if (cancelPromise) return;
      setContainerStatuses(
        res
          .filter((result) => result.isActive === true)
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    });
    return () => {
      cancelPromise = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuestUser, userHomeSite]);

  useEffect(() => {
    if (!defaultLocation || !containers || containers.length === 0) {
      return;
    }

    const missingLocationIndexes = containers.reduce((indexes, container, index) => {
      if (!container?.location && (!container?.locationName || container?.locationName === " ")) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    if (missingLocationIndexes.length === 0) {
      return;
    }

    const newContainers = structuredClone(containers);
    const copyContainerErrorChecks = structuredClone(containerErrorChecks);

    missingLocationIndexes.forEach((index) => {
      newContainers[index].location = defaultLocation;

      if (copyContainerErrorChecks[index]) {
        copyContainerErrorChecks[index].location = false;
      }
    });

    setContainers(newContainers);
    setContainerErrorChecks(copyContainerErrorChecks);
  }, [defaultLocation, containers, containerErrorChecks, setContainers, setContainerErrorChecks]);

  const AddNewBlankContainer = () => {
    const copyContainers = structuredClone(containers);
    const copyContainerErrorChecks = structuredClone(containerErrorChecks);

    let containerOwnerEmail = determineNewContainerOwnerEmail(
      isGuestUser,
      sampleObject,
      newContainerDefaultOwner,
      userEmail,
    );

    const nextContainerNumber =
      copyContainers.length > 0
        ? Math.max(...copyContainers.map((c) => c.containerNumber)) + 1
        : 0;

    const container = {
      sampleName: null,
      tests: null,
      containerNumber: nextContainerNumber,
      containerTypeName: "",
      containerType: null,
      containerStatusName: "",
      containerStatus: containerStatuses.find((status) => status.name === "Confirmed"),
      cylinderSerialNumber: null,
      size: "",
      containerSizeUoM: "",
      uom: null,
      tareWeight: null,
      tareWeightUoM: null,
      currentAmount: "",
      ownerEmail: containerOwnerEmail,
      createdDate: null,
      createdByEmail: null,
      lastModifiedDate: null,
      lastModifiedByEmail: null,
      subLocation: null,
      expirationDate: null,
      nextDispositionDate: null,
      discardOnDisposition: false,
      locationName: "",
      location: !useDefaultLocation ? null : sampleObject
        ? sampleObject.location === null
          ? defaultLocation
          : sampleObject.location
        : defaultLocation,
      parentID: null,
      parentContainer: null,
      returnLocationName: "",
      returnLocation: null,
      comments: null,
    };

    copyContainers.push(container);

    copyContainerErrorChecks.push({
      containerType: null,
      containerStatus: false,
      size: null,
      currentAmount: null,
      uom: null,
      location: container.location === null ? null : false,
      ownerEmail: container.ownerEmail === null || container.ownerEmail === "" ? null : false,
    });

    setContainers(copyContainers);
    setContainerErrorChecks(copyContainerErrorChecks);
  };

  const CopyContainerRowToEnd = (copiedIndex) => {
    const newContainers = structuredClone(containers);
    const newErrors = structuredClone(containerErrorChecks);

    const copiedContainer = Object.assign({}, containers[copiedIndex]);
    const copiedError = Object.assign({}, containerErrorChecks[copiedIndex]);

    const nextCopyNumber =
      newContainers.length > 0
        ? Math.max(...newContainers.map((c) => c.containerNumber)) + 1
        : 0;

    copiedContainer.containerNumber = nextCopyNumber;
    delete copiedContainer.id;

    newContainers.push(copiedContainer);
    newErrors.push(copiedError);

    setContainers(newContainers);
    setContainerErrorChecks(newErrors, copiedIndex);
  };

  const DeleteContainerRow = (index) => {
    const copyContainers = structuredClone(containers);
    const copyContainerErrorChecks = structuredClone(containerErrorChecks);

    copyContainerErrorChecks.splice(index, 1);
    copyContainers.splice(index, 1);

    //redo the container numbering
    //check to make sure that all the containers weren't removed
    if (copyContainers && copyContainers.length > 0) {
      let i = 0;
      do {
        copyContainers[i].containerNumber = i;
        i += 1;
      } while (i < copyContainers.length);
    }

    setContainers(copyContainers);
    setContainerErrorChecks(copyContainerErrorChecks, index);
  };

  const handleContainerRowChange = (property, value, index) => {
    const newContainer = structuredClone(containers);
    const copyContainerErrorChecks = structuredClone(containerErrorChecks);

    newContainer[index][property] = value;

    if (property === "containerType") {
      copyContainerErrorChecks[index].containerType = CheckAutoCompleteField(
        newContainer[index],
        property,
      );

      if (value) {
        newContainer[index].size = value.defaultContainerSize;

        if (value.defaultContainerUoMName) {
          const newUoM = availableUOMs.find(
            (result) => result.uoMName === value.defaultContainerUoMName,
          );

          if (newUoM) {
            newContainer[index].currentAmount = RecalculateCurrentAmountOnUoMChange(
              sampleObject,
              substanceObject,
              newContainer[index].currentAmount,
              newContainer[index].uom,
              newUoM,
            );
            newContainer[index].uom = newUoM;
            newContainer[index].containerSizeUoM = "";
            newContainer[index].containerTypeName = "";

            copyContainerErrorChecks[index].uom = false;
          }

          copyContainerErrorChecks[index].size = CheckContainerSize(newContainer[index]);
        } else {
          newContainer[index].size = "";
          copyContainerErrorChecks[index].size = null;
        }
      } else {
        newContainer[index].size = "";
        newContainer[index].uom = null;
        newContainer[index].containerSizeUoM = "";
        newContainer[index].currentAmount = "";
        newContainer[index].containerTypeName = "";

        copyContainerErrorChecks[index].currentAmount = null;
        copyContainerErrorChecks[index].uom = null;
        copyContainerErrorChecks[index].size = null;
      }

      setContainerErrorChecks(copyContainerErrorChecks, index);
    } else if (property === "uom") {
      if (value && containers[index].uom) {
        const oldUoM = containers[index].uom;

        newContainer[index].currentAmount = RecalculateCurrentAmountOnUoMChange(
          sampleObject,
          substanceObject,
          newContainer[index].currentAmount,
          oldUoM,
          newContainer[index].uom,
        );
        newContainer[index].size = RecalculateInitialSizeOnUoMChange(
          sampleObject,
          substanceObject,
          newContainer[index].size,
          oldUoM,
          newContainer[index].uom,
        );
      }

      updateErrorChecksForContainers(
        property,
        CheckAutoCompleteField(newContainer[index], property),
        index,
      );
    } else if (property === "containerStatus") {
      if (value && value.name === "Shipped") {
        const locationObject = availableLocations.find((obj) => obj.locationName === "XSHIP");

        if (locationObject) {
          newContainer[index].location = locationObject;
          copyContainerErrorChecks[index].location = false;

          setContainerErrorChecks(copyContainerErrorChecks, index);
        }
      }

      updateErrorChecksForContainers(
        property,
        CheckAutoCompleteField(newContainer[index], property),
        index,
      );
    } else if (property === "location") {
      updateErrorChecksForContainers(
        property,
        CheckAutoCompleteField(newContainer[index], property),
        index,
      );
    }

    setContainers(newContainer);
  };

  function updateErrorChecksForContainers(property, value, index) {
    const copyContainerErrorChecks = structuredClone(containerErrorChecks);

    copyContainerErrorChecks[index][property] = value;

    setContainerErrorChecks(copyContainerErrorChecks, index);
  }

  function CheckAutoCompleteField(oContainer, property) {
    return oContainer?.[property] == null;
  }

  function GetContainerStatusValue(oContainer) {
    if (
      oContainer.containerStatus === null &&
      oContainer.containerStatusName !== null &&
      oContainer.containerStatusName.trim() !== ""
    ) {
      return containerStatuses.find((status) => status.name === oContainer.containerStatusName);
    } else if (oContainer.containerStatus) {
      return oContainer.containerStatus;
    } else {
      return null;
    }
  }

  function RoundAmounts(value, index, property) {
    if (isNumber(value)) {
      const newContainers = structuredClone(containers);

      newContainers[index][property] = RoundNumber(value, 2, true);
      setContainers(newContainers);
    }
  }

  function PopulateTests(tests) {
    let returnValue = null;

    if (tests === null) {
      return returnValue;
    }

    tests.forEach((oTest) => {
      if (returnValue === null) {
        returnValue = `${oTest.methodFacility?.methodName}/${oTest.methodFacility?.testFacilityAbv}`;
      } else {
        returnValue += `, ${oTest.methodFacility?.methodName}/${oTest.methodFacility?.testFacilityAbv}`;
      }
    });

    return returnValue;
  }

  function SaveContainerComment() {
    if (containerWorkingIndex !== null) {
      handleContainerRowChange("comments", tempContainerInstruction, containerWorkingIndex);
      setTempContainerInstruction("");
    }
    closeModalComments();
  }

  return (
    <>
      <UXDataTableWithoutBody
        tableWidth={width}
        cols={methodCols}
        blankFirstHeader={enableAdditions || enableDeletions || enableCopy}
        tableId="containerTable"
        rowLength={containers ? containers.length : 0}
        enablePaging={false}
        rowsPerPage={0}
        page={0}
        handleChangePage={null}
        handleChangeRowsPerPage={null}
        noDataFoundMessage={"No containers found"}
        enableAddIcon={enableAdditions}
        disableAddFunction={isRestricted}
        addButtonHelperText={
          message && (
            <Typography sx={{ ml: 1 }} color="#D32F2F">
              {message}
            </Typography>
          )
        }
        addFunction={AddNewBlankContainer}
        addToolTipText={"Add Container"}
        isDataLoading={false}>
        <StyledTableBody>
          {containers &&
            sortedContainers.map((containerInfo, index) => {
              const sourceIndex = containers.findIndex((container) => {
                if (containerInfo?.id && container?.id) {
                  return container.id === containerInfo.id;
                }

                return container.containerNumber === containerInfo.containerNumber;
              });

              const rowIndex = sourceIndex >= 0 ? sourceIndex : index;

              return (
                <TableRow
                  key={`ContainerRow${containerInfo.id ?? "new"}-${containerInfo.containerNumber}`}>
                  {/* containerInfo._originalUoM is used to block certain deletions in blending */}
                  {(enableAdditions || enableDeletions || enableCopy) && (
                    <StyledTableCell style={{ width: "90px" }} component="th" scope="row">
                      {!containerInfo.createdDate &&
                        enableDeletions &&
                        !containerInfo._originalUoM && (
                          <Tooltip title="Delete Container" placement="right">
                            <TrashIcon onClick={() => DeleteContainerRow(rowIndex)} />
                          </Tooltip>
                        )}

                      {enableCopy && (
                        <Tooltip title="Copy Row" placement="right">
                          <ContentCopyIcon
                            style={{ marginLeft: "10px" }}
                            value={rowIndex}
                            onClick={() => CopyContainerRowToEnd(rowIndex)}
                          />
                        </Tooltip>
                      )}
                    </StyledTableCell>
                  )}

                  <StyledTableCell style={{ width: "15px" }}>
                    <b>{containerInfo.containerNumber}</b>
                  </StyledTableCell>
                  {showParentContainer && (
                    <StyledTableCell>
                      <StyledAutocomplete
                        renderOption={(props2, option) => (
                          <Option {...props2}>{option.containerNumber}</Option>
                        )}
                        disabled={
                          readOnly || isRestricted || !(containerInfo.createdDate === null)
                        }
                        disablePortal
                        noOptionsText={"Loading..."}
                        options={sortedParentContainers.filter(
                          (c) => c.containerNumber !== containerInfo.containerNumber,
                        )}
                        getOptionLabel={(option) => option.containerNumber.toString()}
                        onChange={(e, value) =>
                          handleContainerRowChange("parentContainer", value, rowIndex)
                        }
                        autoHighlight
                        autoSelect
                        value={containerInfo.parentContainer}
                        isOptionEqualToValue={(option, value) =>
                          value.containerNumber === option.containerNumber
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            style={{ width: "70px" }}
                            variant="outlined"
                            size="small"
                            inputProps={{
                              ...params.inputProps,
                              style: { fontSize: containerGridFontSize },
                            }}
                            InputProps={{ ...params.InputProps }}
                          />
                        )}
                      />
                    </StyledTableCell>
                  )}
                  {showParentMidas && (
                    <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                      {containerInfo.sampleName
                        ? formatMidasNumber(containerInfo.sampleName)
                        : ""}
                    </StyledTableCell>
                  )}
                  {showWorkRequestTests && (
                    <StyledTableCell>{PopulateTests(containerInfo.tests)}</StyledTableCell>
                  )}

                  <StyledTableCell>
                    <StyledAutocomplete
                      renderOption={(props2, option) => (
                        <Option {...props2}>{option.name}</Option>
                      )}
                      disabled={
                        isRestricted || readOnly
                          ? true
                          : containerInfo.containerStatus
                            ? containerInfo.containerStatus.name.includes("Discard")
                            : false
                      }
                      disablePortal
                      noOptionsText={"Loading Types..."}
                      options={containerTypes}
                      getOptionLabel={(option) => option.name}
                      onChange={(e, value) =>
                        handleContainerRowChange("containerType", value, rowIndex)
                      }
                      value={
                        containerInfo.containerType ??
                        containerTypes.find(
                          (type) => type.name === containerInfo.containerTypeName,
                        ) ??
                        null
                      }
                      isOptionEqualToValue={(option, value) => value.name === option.name}
                      autoHighlight
                      autoSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          style={{ width: "160px" }}
                          variant="outlined"
                          size="small"
                          error={containerErrorChecks[rowIndex]?.containerType}
                          inputProps={{
                            ...params.inputProps,
                            style: { fontSize: containerGridFontSize },
                          }}
                          InputProps={{ ...params.InputProps }}
                        />
                      )}
                    />
                  </StyledTableCell>

                  <StyledTableCell>
                    <StyledAutocomplete
                      renderOption={(props2, option) => (
                        <Option {...props2}>{option.name}</Option>
                      )}
                      disabled={isRestricted || readOnly || lockContainerStatus}
                      disablePortal
                      noOptionsText={"Loading Statuses..."}
                      options={containerStatuses}
                      getOptionLabel={(option) => option.name}
                      onChange={(e, value) =>
                        handleContainerRowChange("containerStatus", value, rowIndex)
                      }
                      value={GetContainerStatusValue(containerInfo)}
                      isOptionEqualToValue={(option, value) => value.name === option.name}
                      autoHighlight
                      autoSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          style={{ width: "145px" }}
                          variant="outlined"
                          size="small"
                          error={containerErrorChecks[rowIndex]?.containerStatus}
                          inputProps={{
                            ...params.inputProps,
                            style: { fontSize: containerGridFontSize },
                          }}
                          InputProps={{ ...params.InputProps }}
                        />
                      )}
                    />
                  </StyledTableCell>

                  <StyledTableCell style={{ width: "80px" }}>
                    <TextField
                      style={{ width: "70px", marginTop: "5px" }}
                      disabled={
                        isRestricted || readOnly
                          ? true
                          : containerInfo.containerStatus
                            ? containerInfo.containerStatus.name.includes("Discard")
                            : false
                      }
                      size="small"
                      margin="dense"
                      variant="outlined"
                      inputProps={{ style: { fontSize: containerGridFontSize } }}
                      InputLabelProps={{ shrink: true }}
                      value={containerInfo.size}
                      error={containerErrorChecks[rowIndex]?.size}
                      onChange={(e) => handleContainerRowChange("size", e.target.value, rowIndex)}
                      onBlur={(e) => {
                        updateErrorChecksForContainers(
                          "size",
                          CheckContainerSize(containerInfo),
                          rowIndex,
                        );
                        RoundAmounts(containerInfo.size, rowIndex, "size");
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "80px" }}>
                    <TextField
                      style={{ width: "70px", marginTop: "5px" }}
                      disabled={
                        isRestricted || readOnly
                          ? true
                          : containerInfo.containerStatus
                            ? containerInfo.containerStatus.name.includes("Discard")
                            : false
                      }
                      size="small"
                      margin="dense"
                      variant="outlined"
                      inputProps={{ style: { fontSize: containerGridFontSize } }}
                      InputLabelProps={{ shrink: true }}
                      value={containerInfo.currentAmount}
                      error={containerErrorChecks[rowIndex]?.currentAmount}
                      onChange={(e) =>
                        handleContainerRowChange("currentAmount", e.target.value, rowIndex)
                      }
                      onBlur={(e) => {
                        updateErrorChecksForContainers(
                          "currentAmount",
                          CheckContainerCurrentAmount(containerInfo),
                          rowIndex,
                        );
                        RoundAmounts(containerInfo.currentAmount, rowIndex, "currentAmount");
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "80px" }}>
                    <StyledAutocomplete
                      renderOption={(props2, option) => (
                        <Option {...props2}>{option.uoMName}</Option>
                      )}
                      disabled={
                        isRestricted || readOnly
                          ? true
                          : containerInfo.containerStatus
                            ? containerInfo.containerStatus.name.includes("Discard")
                            : false
                      }
                      disablePortal
                      noOptionsText={"Loading UOMs..."}
                      options={availableUOMs.filter(
                        (result) =>
                          result.type === "weight" ||
                          result.type === "volume" ||
                          result.type === "unit",
                      )}
                      getOptionLabel={(option) => option.uoMName}
                      onChange={(e, value) => handleContainerRowChange("uom", value, rowIndex)}
                      value={
                        containerInfo.uom ??
                        availableUOMs.find((u) => u.uoMName === containerInfo.containerSizeUoM) ??
                        null
                      }
                      isOptionEqualToValue={(option, value) => value.uoMName === option.uoMName}
                      autoHighlight
                      autoSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          style={{ width: "80px" }}
                          variant="outlined"
                          size="small"
                          error={containerErrorChecks[rowIndex]?.uom}
                          inputProps={{
                            ...params.inputProps,
                            style: { fontSize: containerGridFontSize },
                          }}
                          InputProps={{ ...params.InputProps }}
                        />
                      )}
                    />
                  </StyledTableCell>

                  <TableCell align="center" style={{ width: "75px" }}>
                    {containerInfo.size > 0
                      ? ((containerInfo.currentAmount / containerInfo.size) * 100).toFixed(2)
                      : 0}
                    %
                  </TableCell>

                  <StyledTableCell>
                    <StyledAutocomplete
                      renderOption={(props2, option) => (
                        <Option {...props2}>{option.locationName}</Option>
                      )}
                      disabled={
                        isRestricted || readOnly
                          ? true
                          : containerInfo.containerStatus
                            ? containerInfo.containerStatus.name.includes("Discard")
                            : false
                      }
                      disablePortal
                      noOptionsText={"Loading Locations..."}
                      options={availableLocations}
                      getOptionLabel={(option) => option.locationName}
                      onChange={(e, value) => handleContainerRowChange("location", value, rowIndex)}
                      isOptionEqualToValue={(option, value) =>
                        value.locationName === option.locationName
                      }
                      autoHighlight
                      autoSelect
                      value={
                        containerInfo.location ??
                        availableLocations.find(
                          (l) => l.locationName === containerInfo.locationName,
                        ) ??
                        null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          style={{ width: "160px" }}
                          variant="outlined"
                          size="small"
                          error={containerErrorChecks[rowIndex]?.location}
                          inputProps={{
                            ...params.inputProps,
                            style: { fontSize: containerGridFontSize },
                          }}
                          InputProps={{ ...params.InputProps }}
                        />
                      )}
                    />
                  </StyledTableCell>

                  <StyledTableCell>
                    <TextField
                      style={{ width: "160px", marginTop: "5px" }}
                      disabled={
                        isRestricted || readOnly
                          ? true
                          : containerInfo.containerStatus
                            ? containerInfo.containerStatus.name.includes("Discard")
                            : false
                      }
                      multiline
                      size="small"
                      margin="dense"
                      variant="outlined"
                      InputProps={{ style: { fontSize: containerGridFontSize }, maxLength: 4000 }}
                      InputLabelProps={{ shrink: true }}
                      value={containerInfo.subLocation ?? ""}
                      onChange={(e) =>
                        handleContainerRowChange("subLocation", e.target.value, rowIndex)
                      }
                    />
                  </StyledTableCell>

                  <StyledTableCell>
                    <AuthGate
                      fallback={
                        <TextField
                          style={{ minWidth: "225px", width: "100%", marginTop: "5px" }}
                          disabled
                          size="small"
                          margin="dense"
                          variant="outlined"
                          InputProps={{
                            style: { fontSize: containerGridFontSize },
                            maxLength: 50,
                          }}
                          value={containerInfo.ownerEmail}
                        />
                      }>
                      <EmailAddressTextField
                        style={{ minWidth: "300px", fontSize: "12px" }}
                        validatedUserEmail={containerInfo.ownerEmail}
                        setValidatedUserEmail={(e) =>
                          handleContainerRowChange("ownerEmail", e, rowIndex)
                        }
                        hasErrors={
                          containerErrorChecks[rowIndex]?.ownerEmail === null
                            ? false
                            : containerErrorChecks[rowIndex]?.ownerEmail
                        }
                        setHasErrors={(e) =>
                          updateErrorChecksForContainers("ownerEmail", e, rowIndex)
                        }
                        isDisabled={
                          isRestricted || readOnly
                            ? true
                            : containerInfo.containerStatus
                              ? containerInfo.containerStatus.name.includes("Discard")
                              : false
                        }
                        labelText="Owner Email"
                        placeholderText="Owner Email"
                        showPlusMeButton={true}
                      />
                    </AuthGate>
                  </StyledTableCell>

                  {/* Comments */}
                  <TableCell align="center" style={{ width: "80px" }}>
                    <IntegrationCommentsIcon
                      onClick={() =>
                        openModalComments(
                          "View/Edit Container Comments",
                          "You can add/edit an existing comment from here.",
                          "Save",
                          "Cancel",
                          rowIndex,
                        )
                      }
                    />
                  </TableCell>
                  {showTests && (
                    <StyledTableCell>{PopulateTests(containerInfo.tests)}</StyledTableCell>
                  )}
                </TableRow>
              );
            })}
        </StyledTableBody>
      </UXDataTableWithoutBody>

      <ModalTwoButtons
        title={modalCommentsTitle}
        button1Text={modalCommentsButton1Text}
        button1Action={SaveContainerComment}
        isButton1Disabled={readOnly}
        button2Text={modalCommentsButton2Text}
        button2Action={closeModalComments}
        open={modalCommentsOpen}
        setOpen={setModalCommentsOpen}>
        <label>{modalCommentsText}</label>

        <div />

        <TextField
          size="small"
          multiline
          label="Comments"
          value={tempContainerInstruction === null ? "" : tempContainerInstruction}
          onChange={(e) => {
            setTempContainerInstruction(e.target.value);
          }}
          margin="normal"
          style={{ width: 500, marginTop: 40 }}
          inputProps={{ maxLength: 250 }}
          disabled={readOnly}
        />
      </ModalTwoButtons>
    </>
  );
};

export default ContainerTable;
